import Image from "next/image";
import { usePathname } from "next/navigation";

import Link from "next/link";
import { IconFacebook, IconLinkedIn, IconX, IconInstagram } from "./Icons";
import "./Footer.css";

type FooterLink = { label: string; href: string };

// Mirrors Header.tsx's navItems: same section titles/hrefs, and the same
// top-level menu entries (one level deep — nested submenu items are not
// repeated here, matching the footer's flat-list layout).
const columns: { title: string; href: string; links: FooterLink[] }[] = [
  {
    title: "What We Do",
    href: "/whatwedo",
    links: [
      { label: "Enterprise Transformation", href: "/enterprise-transformation" },
      { label: "SuccessFactors", href: "/enterprise-transformation/successfactors" },
      { label: "Data & SAP BDC", href: "/datasap" },
      { label: "AI & Intelligence", href: "/whatwedo/Ai-and-Intelligence" },
      { label: "Operate & Improve", href: "/Operate" },
    ],
  },
  {
    title: "Knowledge",
    href: "/knowledge",
    links: [
      { label: "Supply Chain & Assets", href: "/knowledge/supply-chain" },
      { label: "Finance & Performance", href: "/knowledge/finance-performance" },
      { label: "Customer & Revenue", href: "/knowledge/customer&revenue" },
      { label: "Vendor Spend", href: "/knowledge/vendor-spend" },
      { label: "Tax, Disclosure & Compliance", href: "/knowledge/tax-disclouser" },
      { label: "People & Talent Intelligence", href: "/knowledge/talent-intelligence" },
      { label: "Office of CXO", href: "/knowledge/cxo" },
    ],
  },
  {
    title: "Industries",
    href: "/industries",
    links: [
      { label: "Energy & Utilities", href: "/industries/energy-utilities" },
      { label: "FMCG & Retail", href: "/industries/fmcg-retail" },
      { label: "Fashion & Lifestyle", href: "/industries/fashion-lifestyle" },
      { label: "Manufacturing", href: "/industries/manufacturing" },
      { label: "Pharma & Life Sciences", href: "/industries/pharma-lifescience" },
      { label: "Hotels & Hospitality", href: "/industries/industry-hotel" },
      { label: "BFSI", href: "/industries/bfsi" },
      { label: "Airlines & Airports", href: "/industries/airlines-airports" },
      { label: "Professional Services", href: "/industries/professional-services" },
    ],
  },
  {
    title: "About",
    href: "/about",
    links: [
      { label: "Our Story", href: "/about/our-story" },
      { label: "Leadership", href: "/about/leadership" },
      { label: "Our People", href: "/about/our-people" },
      { label: "Global Presence", href: "/about/global-presense" },
      { label: "Partnerships & Alliances", href: "/about/partnership" },
      { label: "Quality & Compliance", href: "/about/quality-compliance" },
      { label: "Responsible Business", href: "/about/responsible-business" },
    ],
  },
  {
    title: "Company",
    href: "/about",
    links: [
      { label: "Careers", href: "/careers" },
      { label: "Customers", href: "/customers" },
      { label: "Insights", href: "/insights" },
      { label: "Solutions", href: "/solutions" },
      { label: "52 Problems", href: "/52problem" },
    ],
  },
];

export default function Footer() {
  const pathname = usePathname();

  // Hide the public site footer on the admin dashboard.
  if (pathname?.startsWith("/admin")) {
    return null;
  }

  return (
    <footer className="site-footer">
      <div className="footer-top">
        <div className="footer-brand">
          <Image src="/assets/images/logo.png" alt="Orane Consulting" className="footer-logo" width={140} height={34} style={{ height: "auto" }} />
          <p className="footer-tagline">Real Business. Real Problems. Real Value.</p>
          <div className="socials">
            <a href="https://www.facebook.com/OraneConsulting/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><IconFacebook /></a>
            <a href="https://in.linkedin.com/company/orane-consulting-private-limited" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><IconLinkedIn /></a>
            <a href="https://x.com/OraneConsultin1/status/1403585411784462337" target="_blank" rel="noopener noreferrer" aria-label="X"><IconX /></a>
            <a href="https://www.instagram.com/oraneconsulting/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><IconInstagram /></a>
          </div>
           <span className="footer-copyright">© 2026 Orane Consulting. All rights reserved.</span>
        </div>

        <div className="footer-links">
          {columns.map((column) => (
            <div className="footer-column" key={column.title}>
              <Link href={column.href}>
                <h3>{column.title}</h3>
              </Link>
              {column.links.map((link) => (
                <Link href={link.href} key={link.label}>{link.label}</Link>
              ))}
            </div>
          ))}
        </div>

        <div className="footer-newsletter">
          <h3>Stay Ahead</h3>
          <p>Get the latest insights and perspectives.</p>
          <form>
            <input type="email" placeholder="Your email address" aria-label="Email address" />
            <button type="submit" aria-label="Subscribe">→</button>
          </form>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-bottom-left">
         
          <div className="footer-legal">
            <Link href="/coming-soon">Privacy</Link>
            <span className="footer-divider" aria-hidden="true">|</span>
            <Link href="/coming-soon">Terms</Link>
            <span className="footer-divider" aria-hidden="true">|</span>
            <Link href="/coming-soon">Security</Link>
            <span className="footer-divider" aria-hidden="true">|</span>
            <Link href="/coming-soon">Compliance</Link>
          </div>
        </div>
        <span className="footer-regions">INDIA · AFRICA · MIDDLE EAST · EUROPE · CIS · SOUTHEAST ASIA · AUSTRALIA</span>
      </div>
    </footer>
  );
}
